import { bind as wanakanaBind } from "wanakana";
import monthAnswers from "./months.json";
import dayAnswers from "./days.json";

function randomWithin(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function getLastDayOfMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

interface Question {
  month: number;
  day: number;
}

function getRandomDate(): Question {
  const year = new Date().getFullYear();
  const month = randomWithin(0, 11);
  const day = randomWithin(1, getLastDayOfMonth(year, month));
  return { month: month + 1, day };
}

interface AnswerObject {
  [key: number]: string[];
}

function getPossibleAnswers(month: number, day: number): string[] {
  const monthAnswer = (monthAnswers as AnswerObject)[month];
  const dayAnswer = (dayAnswers as AnswerObject)[day];

  if (!monthAnswer) {
    throw new Error(`Invalid month: ${month}`);
  }
  if (!dayAnswer) {
    throw new Error(`Invalid day: ${day}`);
  }

  const answers: string[] = [];
  monthAnswer.forEach((month) => {
    dayAnswer.forEach((day) => {
      answers.push(`${month}${day}`);
    });
  });
  return answers;
}

interface Stats {
  currentStreak: number;
  longestStreak: number;
}

const EMPTY_STATS: Stats = {
  currentStreak: 0,
  longestStreak: 0,
};

const STATS_KEY = "jd-stats";

function getStats(): Stats {
  try {
    const storedStats = localStorage.getItem(STATS_KEY);
    if (storedStats) {
      const stats = JSON.parse(storedStats);
      return {
        currentStreak: stats.currentStreak || 0,
        longestStreak: stats.longestStreak || 0,
      };
    }
    return EMPTY_STATS;
  } catch (error) {
    return EMPTY_STATS;
  }
}

function setStats(stats: Stats) {
  try {
    localStorage.setItem(STATS_KEY, JSON.stringify(stats));
  } catch (error) {
    console.error("Error saving stats to localStorage", error);
  }
}

class DatesApp {
  private formElement: HTMLFormElement;
  private inputElement: HTMLInputElement;
  private questionElement: HTMLDivElement;
  private answerElement: HTMLDivElement;
  private submitButtonElement: HTMLButtonElement;
  private question!: Question;
  private isResultsScreen: boolean = false;
  private stats: Stats = EMPTY_STATS;
  private streakElement: HTMLDivElement;

  constructor() {
    this.formElement = document.getElementById(
      "answer-form"
    ) as HTMLFormElement;
    this.inputElement = document.getElementById(
      "answer-input"
    ) as HTMLInputElement;
    this.questionElement = document.getElementById(
      "question"
    ) as HTMLDivElement;
    this.answerElement = document.getElementById("answer") as HTMLDivElement;
    this.submitButtonElement = document.getElementById(
      "submit-button"
    ) as HTMLButtonElement;
    this.streakElement = document.getElementById("streak") as HTMLDivElement;

    wanakanaBind(this.inputElement);
    this.init();
  }

  private createQuestion() {
    this.question = getRandomDate();
    this.questionElement.innerText = `${this.question.month}月${this.question.day}日`;
    this.answerElement.innerText = "";
    this.isResultsScreen = false;
    this.inputElement.value = "";
    this.inputElement.disabled = false;
    this.inputElement.focus();
    this.submitButtonElement.textContent = "Submit";
  }

  private checkAnswer() {
    const input = this.inputElement.value;
    const answers = getPossibleAnswers(this.question.month, this.question.day);

    const isCorrect = answers.includes(input);
    this.isResultsScreen = true;
    this.answerElement.innerText = answers.join("、");
    this.inputElement.disabled = true;
    this.submitButtonElement.focus();
    this.submitButtonElement.textContent = "Next";
    if (isCorrect) {
      this.stats.currentStreak++;
      if (this.stats.currentStreak > this.stats.longestStreak) {
        this.stats.longestStreak = this.stats.currentStreak;
      }
      this.answerElement.style.color = "green";
    } else {
      this.stats.currentStreak = 0;
      this.answerElement.style.color = "red";
    }
    this.updateStreak();
    setStats(this.stats);
  }

  private nextScreen() {
    if (this.isResultsScreen) {
      this.createQuestion();
    } else {
      this.checkAnswer();
    }
  }

  private updateStreak() {
    this.streakElement.innerText = `Current Streak: ${this.stats.currentStreak} | Longest Streak: ${this.stats.longestStreak}`;
  }

  private onSubmit(event: Event) {
    event.preventDefault();
    this.nextScreen();
  }

  private onTouchEnd(event: Event) {
    if (this.isResultsScreen) {
      this.nextScreen();
    }
  }

  private init() {
    this.stats = getStats();

    if (this.stats.longestStreak > 0) {
      this.updateStreak();
    }

    this.createQuestion();
    this.formElement.addEventListener("submit", this.onSubmit.bind(this));
    document.addEventListener("touchend", this.onTouchEnd.bind(this));
  }
}

new DatesApp();
